import { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'

const FeatureFlagContext = createContext()

const CACHE_KEY = 'feature_flags_cache'
const CACHE_TIMESTAMP_KEY = 'feature_flags_timestamp'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export function FeatureFlagProvider({ children }) {
  const [flags, setFlags] = useState(() => {
    // Initialize from cache for instant render
    const cached = sessionStorage.getItem(CACHE_KEY)
    const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY)
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp)
      if (age < CACHE_DURATION) {
        return JSON.parse(cached)
      }
    }
    return {}
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const response = await axios.get('/features', { timeout: 5000 })
        let data = response.data

        // Handle Lambda response format
        if (data.statusCode === 200 && data.body) {
          data = JSON.parse(data.body)
        }

        setFlags(data)
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data))
        sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
      } catch (error) {
        console.error('Error fetching feature flags:', error)
        // On error, keep cached values or empty (features disabled)
      } finally {
        setLoading(false)
      }
    }

    fetchFlags()
  }, [])

  const isEnabled = (flagName) => {
    const flag = flags?.[flagName]
    return flag?.enabled === true
  }

  return (
    <FeatureFlagContext.Provider value={{ flags, loading, isEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider')
  }
  return context
}
